import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { useStartInquiry } from "@/lib/useInquiry";

/**
 * SiteLayout — shared marketing chrome (fixed Nav, Footer, sticky mobile CTA)
 * wrapping every public page via <Outlet/>. Keeping the chrome outside the
 * routed content means Nav/Footer never remount on navigation, so only the
 * page body transitions.
 *
 * The sticky mobile CTA is a transparent, distorted glass pill with a fast
 * white light circling its edge. While the user scrolls it eases down to a
 * compact pill (so it stops covering content), then eases back to full width
 * a beat after scrolling stops — a single buttery width transition.
 */
export default function SiteLayout() {
    const startInquiry = useStartInquiry();
    const [scrolling, setScrolling] = useState(false);
    const idleTimer = useRef(null);

    useEffect(() => {
        const onScroll = () => {
            setScrolling(true);
            if (idleTimer.current) clearTimeout(idleTimer.current);
            idleTimer.current = setTimeout(() => setScrolling(false), 280);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (idleTimer.current) clearTimeout(idleTimer.current);
        };
    }, []);

    return (
        <>
            <Nav onStartProject={startInquiry} />
            <main id="main" data-testid="site-main" className="pg-seamless-dark">
                <Outlet />
            </main>
            <Footer />

            {/* Sticky mobile CTA — site-wide conversion anchor on touch layouts. */}
            <button
                type="button"
                onClick={startInquiry}
                data-testid="sticky-mobile-cta"
                data-scrolling={scrolling ? "true" : "false"}
                className="pg-sticky-cta pg-btn-glass-lume pg-safe-bottom fixed bottom-5 left-1/2 z-40 md:hidden"
                aria-label="Start a project"
            >
                <span>Start A Project</span>
            </button>
        </>
    );
}
